import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Shield, UserPlus, Key, Trash2, RotateCcw, TrendingUp, Activity, Users as UsersIcon } from 'lucide-react';
import { useAdmin } from '../../contexts/AdminContext';
import { createAdmin } from '../../services/admin/authService';
import { toast } from '../../hooks/use-toast';
import {
  getAdminCount,
  getAllAdmins,
  deleteAdmin,
  resetAdminPassword,
  getAdminActivity,
  getAdminProductivity,
  getAllAdminsProductivity
} from '../../services/admin/adminManagementService';

const AdminSettings = () => {
  const { admin } = useAdmin();
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [newAdmin, setNewAdmin] = useState({
    username: '',
    password: '',
    email: '',
    roles: {
      finances: false,
      operations: false,
      customer_support: false
    }
  });
  const [loading, setLoading] = useState(false);
  
  // Admin Management States
  const [adminCount, setAdminCount] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showActivity, setShowActivity] = useState(false);
  const [adminActivity, setAdminActivity] = useState(null);
  const [showProductivity, setShowProductivity] = useState(false);
  const [productivity, setProductivity] = useState(null);
  const [loadingAdmins, setLoadingAdmins] = useState(false);

  const fetchAdminCount = async () => {
    const result = await getAdminCount();
    if (result.success) {
      setAdminCount(result.data);
    }
  };

  const fetchAdmins = async () => {
    setLoadingAdmins(true);
    const result = await getAllAdmins(includeInactive);
    if (result.success) {
      setAdmins(result.data.admins);
    }
    setLoadingAdmins(false);
  };

  // Fetch admin count and list on mount
  useEffect(() => {
    if (admin?.is_super_admin) {
      fetchAdminCount();
      fetchAdmins();
    }
  }, [admin, includeInactive]);

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    
    if (!newAdmin.username || !newAdmin.password) {
      toast({ title: 'Error', description: 'Username and password are required', variant: 'destructive' });
      return;
    }

    setLoading(true);
    const result = await createAdmin(newAdmin);
    
    if (result.success) {
      toast({ title: 'Success', description: 'Admin created successfully' });
      setShowCreateAdmin(false);
      setNewAdmin({
        username: '',
        password: '',
        email: '',
        roles: { finances: false, operations: false, customer_support: false }
      });
      // Refresh admin list
      fetchAdminCount();
      fetchAdmins();
    } else {
      // Handle error message properly
      let errorMessage = 'Failed to create admin';
      
      if (typeof result.message === 'string') {
        errorMessage = result.message;
      } else if (Array.isArray(result.message)) {
        // Handle Pydantic validation errors
        errorMessage = result.message.map(err => {
          if (typeof err === 'object' && err.msg) {
            return `${err.loc?.join(' -> ') || 'Error'}: ${err.msg}`;
          }
          return String(err);
        }).join(', ');
      } else if (typeof result.message === 'object') {
        errorMessage = JSON.stringify(result.message);
      }
      
      toast({ title: 'Error', description: errorMessage, variant: 'destructive' });
    }
    
    setLoading(false);
  };

  const handleDeleteAdmin = async (adminId, username) => {
    if (!window.confirm(`Are you sure you want to delete admin "${username}"? This will deactivate their account.`)) {
      return;
    }

    const result = await deleteAdmin(adminId);
    if (result.success) {
      toast({ title: 'Success', description: result.message });
      fetchAdminCount();
      fetchAdmins();
    } else {
      toast({ title: 'Error', description: result.message, variant: 'destructive' });
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!newPassword || newPassword.length < 6) {
      toast({ title: 'Error', description: 'Password must be at least 6 characters', variant: 'destructive' });
      return;
    }

    const result = await resetAdminPassword(selectedAdmin.id, newPassword);
    if (result.success) {
      toast({ title: 'Success', description: result.message });
      setShowResetPassword(false);
      setSelectedAdmin(null);
      setNewPassword('');
    } else {
      toast({ title: 'Error', description: result.message, variant: 'destructive' });
    }
  };

  const viewActivity = async (adminUser) => {
    setSelectedAdmin(adminUser);
    setShowActivity(true);
    const result = await getAdminActivity(adminUser.id);
    if (result.success) {
      setAdminActivity(result.data);
    }
  };

  const viewProductivity = async (adminUser) => {
    setSelectedAdmin(adminUser);
    setShowProductivity(true);
    const result = await getAdminProductivity(adminUser.id);
    if (result.success) {
      setProductivity(result.data);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-800">Settings</h2>
        <p className="text-gray-600">Manage admin accounts and system settings</p>
      </div>

      {/* Current Admin Info */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5" />
          Your Account
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600">Username</p>
            <p className="font-medium">{admin?.username}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Role</p>
            <Badge className="bg-green-600">
              {admin?.is_super_admin ? 'Super Admin' : 'Admin'}
            </Badge>
          </div>
          {admin?.email && (
            <div>
              <p className="text-sm text-gray-600">Email</p>
              <p className="font-medium">{admin.email}</p>
            </div>
          )}
          <div>
            <p className="text-sm text-gray-600">Permissions</p>
            <div className="flex gap-2 flex-wrap mt-1">
              {admin?.is_super_admin ? (
                <Badge className="bg-purple-600">All Permissions</Badge>
              ) : (
                <>
                  {admin?.roles?.finances && <Badge className="bg-blue-500">Finances</Badge>}
                  {admin?.roles?.operations && <Badge className="bg-green-500">Operations</Badge>}
                  {admin?.roles?.customer_support && <Badge className="bg-orange-500">Support</Badge>}
                </>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Create Admin Section (Super Admin Only) */}
      {admin?.is_super_admin && (
        <>
          {/* Admin Count Overview */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <UsersIcon className="w-5 h-5" />
              Admin Overview
            </h3>
            {adminCount ? (
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-600 font-medium">Total Admins</p>
                  <p className="text-3xl font-bold text-blue-700">{adminCount.total}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <p className="text-sm text-green-600 font-medium">Active</p>
                  <p className="text-3xl font-bold text-green-700">{adminCount.active}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-600 font-medium">Inactive</p>
                  <p className="text-3xl font-bold text-gray-700">{adminCount.inactive}</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                  <p className="text-sm text-purple-600 font-medium">Super Admins</p>
                  <p className="text-3xl font-bold text-purple-700">{adminCount.super_admins}</p>
                </div>
              </div>
            ) : (
              <p className="text-gray-500">Loading admin statistics...</p>
            )}
          </Card>

          {/* Admin Management */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <UserPlus className="w-5 h-5" />
                Admin Management
              </h3>
              <div className="flex gap-3">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={includeInactive}
                    onChange={(e) => setIncludeInactive(e.target.checked)}
                    className="w-4 h-4"
                  />
                  Show Inactive
                </label>
                {!showCreateAdmin && (
                  <Button
                    onClick={() => setShowCreateAdmin(true)}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    Create New Admin
                  </Button>
                )}
              </div>
            </div>

            {showCreateAdmin && (
              <form onSubmit={handleCreateAdmin} className="space-y-4 border-t pt-4 mb-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Username *</label>
                    <Input
                      value={newAdmin.username}
                      onChange={(e) => setNewAdmin({ ...newAdmin, username: e.target.value })}
                      placeholder="Enter username"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Email</label>
                    <Input
                      type="email"
                      value={newAdmin.email}
                      onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                      placeholder="Enter email (optional)"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Password *</label>
                  <Input
                    type="password"
                    value={newAdmin.password}
                    onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                    placeholder="Enter password"
                    required
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Permissions</label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={newAdmin.roles.finances}
                        onChange={(e) => setNewAdmin({
                          ...newAdmin,
                          roles: { ...newAdmin.roles, finances: e.target.checked }
                        })}
                        className="w-4 h-4"
                      />
                      <span>Finances - Access financial data and reports</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={newAdmin.roles.operations}
                        onChange={(e) => setNewAdmin({
                          ...newAdmin,
                          roles: { ...newAdmin.roles, operations: e.target.checked }
                        })}
                        className="w-4 h-4"
                      />
                      <span>Operations - Manage users and verifications</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={newAdmin.roles.customer_support}
                        onChange={(e) => setNewAdmin({
                          ...newAdmin,
                          roles: { ...newAdmin.roles, customer_support: e.target.checked }
                        })}
                        className="w-4 h-4"
                      />
                      <span>Customer Support - Handle reports and complaints</span>
                    </label>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    type="submit"
                    disabled={loading}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {loading ? 'Creating...' : 'Create Admin'}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      setShowCreateAdmin(false);
                      setNewAdmin({
                        username: '',
                        password: '',
                        email: '',
                        roles: { finances: false, operations: false, customer_support: false }
                      });
                    }}
                    variant="outline"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            )}

            {/* Admin List */}
            <div className="border-t pt-4">
              {loadingAdmins ? (
                <p className="text-gray-500 text-center py-4">Loading admins...</p>
              ) : admins.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No admins found</p>
              ) : (
                <div className="space-y-3">
                  {admins.map((adminUser) => (
                    <div
                      key={adminUser.id}
                      className={`border rounded-lg p-4 ${
                        !adminUser.is_active ? 'bg-gray-50 border-gray-300' : 'bg-white'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="font-semibold text-gray-800">{adminUser.username}</h4>
                            {adminUser.is_super_admin && (
                              <Badge className="bg-purple-600">Super Admin</Badge>
                            )}
                            {!adminUser.is_active && (
                              <Badge className="bg-gray-500">Inactive</Badge>
                            )}
                          </div>
                          {adminUser.email && (
                            <p className="text-sm text-gray-600 mb-2">{adminUser.email}</p>
                          )}
                          <div className="flex gap-2 flex-wrap">
                            {adminUser.roles?.finances && (
                              <Badge className="bg-blue-500">Finances</Badge>
                            )}
                            {adminUser.roles?.operations && (
                              <Badge className="bg-green-500">Operations</Badge>
                            )}
                            {adminUser.roles?.customer_support && (
                              <Badge className="bg-orange-500">Support</Badge>
                            )}
                          </div>
                          <div className="mt-2 text-xs text-gray-500">
                            {adminUser.last_activity ? (
                              <span>Last active: {new Date(adminUser.last_activity).toLocaleString()}</span>
                            ) : (
                              <span>No activity recorded</span>
                            )}
                            <span className="mx-2">•</span>
                            <span>{adminUser.actions_last_30_days} actions in last 30 days</span>
                          </div>
                        </div>
                        {adminUser.id !== admin.id && !adminUser.is_super_admin && (
                          <div className="flex gap-2 ml-4">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => viewActivity(adminUser)}
                              className="text-blue-600 border-blue-300 hover:bg-blue-50"
                            >
                              <Activity className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => viewProductivity(adminUser)}
                              className="text-green-600 border-green-300 hover:bg-green-50"
                            >
                              <TrendingUp className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedAdmin(adminUser);
                                setShowResetPassword(true);
                              }}
                              className="text-orange-600 border-orange-300 hover:bg-orange-50"
                            >
                              <RotateCcw className="w-4 h-4" />
                            </Button>
                            {adminUser.is_active && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeleteAdmin(adminUser.id, adminUser.username)}
                                className="text-red-600 border-red-300 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </>
      )}

      {/* Reset Password Modal */}
      {showResetPassword && selectedAdmin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Reset Password for {selectedAdmin.username}</h3>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">New Password</label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 6 characters)"
                  required
                />
              </div>
              <div className="flex gap-3">
                <Button type="submit" className="bg-orange-600 hover:bg-orange-700">
                  Reset Password
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowResetPassword(false);
                    setSelectedAdmin(null);
                    setNewPassword('');
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {/* Activity Modal */}
      {showActivity && selectedAdmin && adminActivity && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-auto">
          <Card className="p-6 max-w-4xl w-full mx-4 my-8 max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Activity History - {selectedAdmin.username}</h3>
              <Button
                variant="outline"
                onClick={() => {
                  setShowActivity(false);
                  setSelectedAdmin(null);
                  setAdminActivity(null);
                }}
              >
                Close
              </Button>
            </div>
            
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-blue-50 p-3 rounded border border-blue-200">
                <p className="text-sm text-blue-600">Total Actions</p>
                <p className="text-2xl font-bold text-blue-700">{adminActivity.total_count}</p>
              </div>
              <div className="bg-green-50 p-3 rounded border border-green-200">
                <p className="text-sm text-green-600">Action Types</p>
                <p className="text-2xl font-bold text-green-700">
                  {Object.keys(adminActivity.action_counts).length}
                </p>
              </div>
              <div className="bg-purple-50 p-3 rounded border border-purple-200">
                <p className="text-sm text-purple-600">Resources</p>
                <p className="text-2xl font-bold text-purple-700">
                  {Object.keys(adminActivity.resource_counts).length}
                </p>
              </div>
            </div>

            <div className="space-y-3 max-h-96 overflow-auto">
              {adminActivity.activities.map((activity, idx) => (
                <div key={idx} className="border rounded p-3 bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-gray-800">
                        <Badge className="mr-2">{activity.action}</Badge>
                        {activity.resource_type}
                      </p>
                      {activity.resource_id && (
                        <p className="text-sm text-gray-600">ID: {activity.resource_id}</p>
                      )}
                      {activity.details && Object.keys(activity.details).length > 0 && (
                        <p className="text-sm text-gray-500 mt-1">
                          {JSON.stringify(activity.details)}
                        </p>
                      )}
                    </div>
                    <span className="text-xs text-gray-500">
                      {new Date(activity.timestamp).toLocaleString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* Productivity Modal */}
      {showProductivity && selectedAdmin && productivity && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="p-6 max-w-2xl w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                Productivity Stats - {selectedAdmin.username}
              </h3>
              <Button
                variant="outline"
                onClick={() => {
                  setShowProductivity(false);
                  setSelectedAdmin(null);
                  setProductivity(null);
                }}
              >
                Close
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-blue-50 p-4 rounded border border-blue-200">
                <p className="text-sm text-blue-600">Total Actions</p>
                <p className="text-3xl font-bold text-blue-700">{productivity.total_actions}</p>
                <p className="text-xs text-blue-500 mt-1">Last {productivity.period_days} days</p>
              </div>
              <div className="bg-green-50 p-4 rounded border border-green-200">
                <p className="text-sm text-green-600">Actions Per Day</p>
                <p className="text-3xl font-bold text-green-700">{productivity.actions_per_day}</p>
                <p className="text-xs text-green-500 mt-1">Average</p>
              </div>
            </div>

            {productivity.most_active_day?.date && (
              <div className="bg-purple-50 p-4 rounded border border-purple-200 mb-4">
                <p className="text-sm text-purple-600 font-medium">Most Active Day</p>
                <p className="text-lg font-bold text-purple-700">
                  {productivity.most_active_day.date}
                </p>
                <p className="text-sm text-purple-600">
                  {productivity.most_active_day.actions} actions
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="font-semibold mb-2">By Action</h4>
                <div className="space-y-1">
                  {Object.entries(productivity.by_action).map(([action, count]) => (
                    <div key={action} className="flex justify-between text-sm">
                      <span className="text-gray-600">{action}</span>
                      <span className="font-medium">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="font-semibold mb-2">By Resource</h4>
                <div className="space-y-1">
                  {Object.entries(productivity.by_resource).map(([resource, count]) => (
                    <div key={resource} className="flex justify-between text-sm">
                      <span className="text-gray-600">{resource}</span>
                      <span className="font-medium">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Service Charge Information */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Service Charge Configuration</h3>
        <div className="space-y-3">
          <div className="border-l-4 border-blue-500 bg-blue-50 p-4">
            <p className="font-medium text-blue-900">Economy</p>
            <p className="text-sm text-blue-700">K1,500 per amount &lt; K10,000</p>
            <p className="text-sm text-blue-700">K1,500 per each K10,000 for amounts above</p>
          </div>
          <div className="border-l-4 border-purple-500 bg-purple-50 p-4">
            <p className="font-medium text-purple-900">Premium</p>
            <p className="text-sm text-purple-700">K2,000 per amount &lt; K10,000</p>
            <p className="text-sm text-purple-700">K2,000 per each K10,000 for amounts above</p>
          </div>
          <div className="border-l-4 border-orange-500 bg-orange-50 p-4">
            <p className="font-medium text-orange-900">Bike</p>
            <p className="text-sm text-orange-700">K500 per amount &lt; K5,000</p>
            <p className="text-sm text-orange-700">K500 per each K5,000 for amounts above</p>
          </div>
        </div>
      </Card>

      {/* System Information */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Key className="w-5 h-5" />
          System Information
        </h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-600">Platform</p>
            <p className="font-medium">Yachangu Admin Portal</p>
          </div>
          <div>
            <p className="text-gray-600">Version</p>
            <p className="font-medium">1.0.0</p>
          </div>
          <div>
            <p className="text-gray-600">Environment</p>
            <p className="font-medium">Production</p>
          </div>
          <div>
            <p className="text-gray-600">Last Updated</p>
            <p className="font-medium">{new Date().toLocaleDateString()}</p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AdminSettings;
